import { Box, Button, LinearProgress } from '@mui/material'
import React, { memo, useCallback } from 'react'

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

import { useAppStore } from '../../store/useAppStore'
import useDamageRegistrationStep from '../../hooks/damageRegistration/useDamageRegistrationStep'
import { removeMime } from '../../lib/helpers'
import useDamageMutation from '../../hooks/mutations/useDamageMutation'
import { useTranslation } from 'react-i18next'
import { VEHICLE_SIDES } from '../../constants'

function DamageRegistrationPage({
  vehicleId,
  depotCode,
  pNumber,
}: {
  vehicleId: string
  depotCode: string
  pNumber?: string
}) {
  const tabValue = useAppStore((state) => state.tabValue)
  const damageRegistrationStep = useAppStore(
    (state) => state.damageRegistrationStep
  )
  const setDamageRegistrationStep = useAppStore(
    (state) => state.setDamageRegistrationStep
  )

  // Custom hook which separate buisness logic and handle some UI usage.
  const {
    isButtonDisabled,
    coordinates,
    damageTypes,
    damagePhotos,
    descriptionInput,
    progress,
    currentStepScreen,
  } = useDamageRegistrationStep(damageRegistrationStep)

  const { sendDamage } = useDamageMutation()

  const { t } = useTranslation()

  const currentSide = VEHICLE_SIDES[tabValue]

  const handleDamageSending = () => {
    const [x, y] = coordinates as [number, number]

    // Convert to ISO 8601 format
    const timestamp = new Date().toISOString()
    const body = {
      side: currentSide,
      depotCode,
      xCoordinate: parseInt(`${x}`, 10),
      yCoordinate: parseInt(`${y}`, 10),
      classifications: Object.keys(damageTypes).filter(
        (type) => damageTypes[type]
      ),
      pNumber: pNumber || '',
      description: descriptionInput,
      pictures: damagePhotos.map((photo) => {
        return removeMime(photo)
      }),
      licensePlate: vehicleId,
      status: 'nieuw',
      voortgang: 'open',
      timestamp: timestamp.replace('Z', ''),
      claimUploaded: false,
    }

    sendDamage(body)
  }

  const goToNextStep = useCallback(() => {
    setDamageRegistrationStep((step: number) => step + 1)
  }, [setDamageRegistrationStep])

  const backToPreviousStep = useCallback(
    () => setDamageRegistrationStep((step: number) => step - 1),
    [setDamageRegistrationStep]
  )

  return (
    <>
      <Box
        mx={-2}
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
      >
        {currentStepScreen}
        <Box
          sx={{
            width: '100%',
            position: 'absolute',
            bottom: 0,
            backgroundColor: '#fff',
            zIndex: '10',
            '& .Mui-disabled': {
              pointerEvents: 'initial',
            },
          }}
        >
          <LinearProgress
            variant="determinate"
            sx={{ height: 2 }}
            value={progress}
          />
          <Box
            display="flex"
            justifyContent={
              damageRegistrationStep > 1 ? 'space-between' : 'flex-end'
            }
            px={2}
            py={1.75}
          >
            {damageRegistrationStep > 1 && (
              <Button
                sx={{
                  textTransform: 'none',
                }}
                color="primary"
                onClick={backToPreviousStep}
              >
                {t('footer.back')}
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disabled={isButtonDisabled}
              {...(damageRegistrationStep === 4
                ? { onClick: handleDamageSending }
                : {
                    endIcon: <KeyboardArrowRightIcon />,
                    onClick: goToNextStep,
                  })}
            >
              {damageRegistrationStep === 4
                ? t('common.damage_registration')
                : t('footer.next')}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default memo(DamageRegistrationPage)
